<template>
    <div class="seller-picker">
        <label v-if="heading" class="seller-picker__heading">{{ heading }}</label>

        <!-- Searchable dropdown when the configured sellers exceed the threshold -->
        <template v-if="useDropdown">
            <select
                ref="selectElement"
                :name="name"
                class="form-control"
                v-model="selectedId"
            >
                <option value="">{{ placeholder }}</option>
                <option v-for="seller in sellers" :key="seller.id" :value="seller.id">
                    {{ seller.firstName }}
                </option>
            </select>
        </template>

        <!-- Avatar grid for a handful of sellers -->
        <template v-else>
            <input type="hidden" :name="name" :value="selectedId" />
            <div class="seller-picker__grid">
                <button
                    v-for="seller in sellers"
                    :key="seller.id"
                    type="button"
                    class="seller-picker__option"
                    :class="{ 'seller-picker__option--selected': selectedId === seller.id }"
                    @click="select(seller.id)"
                >
                    <span
                        class="seller-picker__avatar"
                        :style="selectedId === seller.id ? { borderColor: selectedColor, backgroundColor: selectedColor } : {}"
                    >
                        <img
                            v-if="seller.avatarUrl"
                            :src="seller.avatarUrl"
                            :alt="seller.firstName"
                            class="seller-picker__avatar-img"
                        />
                        <span
                            v-else
                            class="seller-picker__initials"
                            :style="selectedId === seller.id ? { color: '#ffffff' } : {}"
                        >{{ seller.initials }}</span>
                    </span>
                    <span class="seller-picker__name">{{ seller.firstName }}</span>
                </button>
            </div>
        </template>
    </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from "vue";

interface Seller {
    id: number;
    firstName: string;
    avatarUrl?: string | null;
    initials: string;
}

interface Props {
    name?: string;
    heading?: string;
    sellers?: Seller[];
    preselectedId?: number | null;
    value?: number | null;
    required?: boolean;
    collapseThreshold?: number;
    placeholder?: string;
    selectedColor?: string;
}

const props = withDefaults(defineProps<Props>(), {
    name: "sales_id",
    heading: "",
    sellers: () => [],
    preselectedId: null,
    value: null,
    required: false,
    collapseThreshold: 6,
    placeholder: "Sök kontakt",
    selectedColor: "#007bff",
});
const selectElement = ref<HTMLSelectElement | null>(null);
const selectedId = ref<number | null>(props.value !== null ? props.value : props.preselectedId);
const useDropdown = computed(() => props.sellers.length > props.collapseThreshold);
let tomSelect: any = null;

function select(id: number): void {
    selectedId.value = selectedId.value === id ? null : id;
}

onMounted(() => {
    if (useDropdown.value && window.TomSelect && selectElement.value) {
        tomSelect = new window.TomSelect(selectElement.value, { allowEmptyOption: true });
        tomSelect.on("change", (value: string) => {
            selectedId.value = value ? Number(value) : null;
        });
        if (selectedId.value) {
            tomSelect.setValue(String(selectedId.value), true);
        }
    }
});

onBeforeUnmount(() => tomSelect?.destroy());
</script>

<style lang="scss" scoped>
.seller-picker {
    &__heading {
        display: block;
        font-weight: normal;
        text-align: left;
        margin-bottom: 0.75rem;
    }

    &__grid {
        display: flex;
        flex-wrap: wrap;
        justify-content: center;
        gap: 0.75rem;
    }

    &__option {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 0.35rem;
        background: none;
        border: none;
        padding: 0;
        cursor: pointer;
        flex: 0 0 calc(25% - 0.75rem);
        max-width: calc(25% - 0.75rem);
    }

    &__avatar {
        width: 3rem;
        height: 3rem;
        border-radius: 50%;
        overflow: hidden;
        display: flex;
        align-items: center;
        justify-content: center;
        background: #e9ecef;
        border: 2px solid transparent;
        transition: border-color 0.15s ease-in-out;
    }

    &__avatar-img {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }

    &__initials {
        font-weight: 600;
        color: #495057;
        text-transform: uppercase;
    }

    &__name {
        font-size: 0.7rem;
        line-height: 1.2;
        text-align: center;
        max-width: 100%;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
}
</style>
